/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import {
	IInspectorNode_Default,
	isInspectorNodeDefault,
	isInspectorNodeList,
	isInspectorNodeMap,
	TInspectorNode
} from "../../InspectorNodeTypes";
import { InspectorNodeDefault } from "../InspectorNodeDefault";
import { InspectorNodeList } from "../InspectorNodeList";
import { useInspectorNode } from "../../../ui/hooks/useInspectorNode";
import { dataEqual } from "@adapptio/lib-blueprint";
import { isEmptyNode, isLabelNode } from "../../utils";
import { isFunction } from "@adapptio/lib-shared";

export interface IInspectorNodeProps {
	node?: TInspectorNode;
	getNode?: () => TInspectorNode;
	depth?: number;
	children?: React.ReactNode | ((node: TInspectorNode) => JSX.Element);
	getUpdateRef?: (node: TInspectorNode) => boolean | string | number;
}

const NODE_ID_PLACEHOLDER = "N";

function arePropsEqual(
	prevProps: Readonly<IInspectorNodeProps>,
	nextProps: Readonly<IInspectorNodeProps>
): boolean {
	if (!dataEqual(prevProps?.depth, nextProps?.depth)) {
		return false;
	}

	const prevNode = isFunction(prevProps.getNode) ? prevProps.getNode() : prevProps.node;
	const nextNode = isFunction(nextProps.getNode) ? nextProps.getNode() : nextProps.node;

	if (!dataEqual(prevNode.key, nextNode.key)) {
		return false;
	}

	if (!dataEqual(prevNode.deps?.length, nextNode.deps?.length)) {
		return false;
	}

	if (!dataEqual(prevNode?.label, nextNode?.label)) {
		return false;
	}

	if (isEmptyNode(prevNode) && isEmptyNode(nextNode)) {
		return false;
	}

	if (isLabelNode(prevNode) && isLabelNode(nextNode)) {
		return false;
	}

	if (isInspectorNodeDefault(prevNode) && isInspectorNodeDefault(nextNode)) {
		if (
			!dataEqual(
				(prevNode as IInspectorNode_Default).children.length,
				(nextNode as IInspectorNode_Default).children.length
			)
		) {
			return false;
		}
	}

	const prevDepsId = prevNode.deps
		.reduce((acc, dep) => (dep ? [ ...acc, dep.nodeId || NODE_ID_PLACEHOLDER ] : acc), [])
		.join(",");
	const nextDepsId = nextNode.deps
		.reduce((acc, dep) => (dep ? [ ...acc, dep.nodeId || NODE_ID_PLACEHOLDER ] : acc), [])
		.join(",");

	if (!dataEqual(prevDepsId, nextDepsId)) {
		return false;
	}

	if (isFunction(nextProps.getUpdateRef)) {
		const nextUpdateRef = nextProps.getUpdateRef(nextNode);

		if (isFunction(prevProps.getUpdateRef)) {
			const prevUpdateRef = prevProps.getUpdateRef(prevNode);

			return nextUpdateRef === prevUpdateRef;
		}

		return false;
	}

	return true;
}

const InspectorNode: React.FC<IInspectorNodeProps> = ({ node: nodeProp, getNode, depth, children }) => {
	const node = isFunction(getNode) ? getNode() : nodeProp;

	useInspectorNode(node);

	if (isFunction(children)) {
		return children(node) as JSX.Element;
	}

	if (isInspectorNodeDefault(node)) {
		return (
			<InspectorNodeDefault node={node} depth={depth}>
				{children}
			</InspectorNodeDefault>
		);
	}

	if (isInspectorNodeList(node) || isInspectorNodeMap(node)) {
		return <InspectorNodeList node={node} />;
	}

	return null;
};

InspectorNode.defaultProps = {
	depth: 0
};

const InspectorNodeMemo = React.memo(InspectorNode, arePropsEqual);

export { InspectorNodeMemo as InspectorNode };
